import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { mapAPI, routeAPI } from '../../utils/api';

export const fetchRoutesInArea = createAsyncThunk(
  'map/fetchRoutesInArea',
  async ({ bounds, limit = 50, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await mapAPI.getRoutesInArea(bounds, limit, offset);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch routes');
    }
  }
);

export const fetchVideosInArea = createAsyncThunk(
  'map/fetchVideosInArea',
  async ({ bounds, limit, offset }, { rejectWithValue }) => {
    try {
      const response = await mapAPI.getVideosInArea(bounds, limit, offset);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch videos');
    }
  }
);

export const fetchAllVideos = createAsyncThunk(
  'map/fetchAllVideos',
  async ({ limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      // Используем границы всего мира для получения всех видео
      const bounds = {
        minLat: -90,
        maxLat: 90,
        minLng: -180,
        maxLng: 180,
      };
      const response = await mapAPI.getVideosInArea(bounds, limit, offset);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch videos');
    }
  }
);

export const fetchUserRoutes = createAsyncThunk(
  'map/fetchUserRoutes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await routeAPI.getUserRoutes();
      console.log('fetchUserRoutes response:', response);
      console.log('fetchUserRoutes response.data:', response.data);
      
      // API может возвращать массив напрямую или объект с полем routes/data
      let routes = response.data;
      if (routes && typeof routes === 'object' && !Array.isArray(routes)) {
        routes = routes.routes || routes.data || [];
      }
      
      return Array.isArray(routes) ? routes : [];
    } catch (error) {
      console.error('fetchUserRoutes error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user routes');
    }
  }
);

const mapSlice = createSlice({
  name: 'map',
  initialState: {
    selectedMap: 'google', // google, yandex, mapbox
    currentView: {
      center: [48.2082, 16.3738], // Vienna by default
      zoom: 5,
    },
    routes: [],
    videos: [],
    videosPagination: {
      hasMore: false,
      currentOffset: 0,
      limit: 20,
      total: 0,
    },
    selectedRoute: null,
    selectedVideo: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    setSelectedMap: (state, action) => {
      state.selectedMap = action.payload;
    },
    setCurrentView: (state, action) => {
      state.currentView = action.payload;
    },
    setSelectedRoute: (state, action) => {
      state.selectedRoute = action.payload;
    },
    setSelectedVideo: (state, action) => {
      state.selectedVideo = action.payload;
    },
    clearSelection: (state) => {
      state.selectedRoute = null;
      state.selectedVideo = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    addRoute: (state, action) => {
      if (!action.payload || !action.payload.id) {
        console.warn('addRoute: Invalid route data', action.payload);
        return;
      }
      
      // Гарантируем, что state.routes всегда массив
      if (!Array.isArray(state.routes)) {
        console.warn('addRoute: state.routes is not an array, initializing as empty array', state.routes);
        state.routes = [];
      }
      
      // Проверяем, нет ли уже такого маршрута
      const existingIndex = state.routes.findIndex(route => route && route.id === action.payload.id);
      if (existingIndex === -1) {
        state.routes.push(action.payload);
      } else {
        state.routes[existingIndex] = action.payload;
      }
    },
    removeRoute: (state, action) => {
      // Гарантируем, что state.routes всегда массив
      if (!Array.isArray(state.routes)) {
        console.warn('removeRoute: state.routes is not an array, initializing as empty array', state.routes);
        state.routes = [];
        return;
      }
      // Удаляем маршрут по ID
      const routeId = typeof action.payload === 'number' ? action.payload : action.payload?.id;
      if (routeId) {
        state.routes = state.routes.filter(route => route && route.id !== routeId);
      }
    },
    clearVideos: (state) => {
      state.videos = [];
      state.videosPagination = {
        hasMore: false,
        currentOffset: 0,
        limit: 20,
        total: 0,
      };
    },
    updateVideoInList: (state, action) => {
      // Обновляем конкретное видео в списке
      const { videoId, updates } = action.payload;
      if (!videoId || !updates) {
        return;
      }
      
      // Гарантируем, что state.videos всегда массив
      if (!Array.isArray(state.videos)) {
        state.videos = [];
        return;
      }
      
      const videoIndex = state.videos.findIndex(v => v && v.id === videoId);
      if (videoIndex !== -1) {
        // Обновляем видео, сохраняя существующие поля
        state.videos[videoIndex] = {
          ...state.videos[videoIndex],
          ...updates
        };
      }
    },
    updateRouteInList: (state, action) => {
      // Обновляем конкретный маршрут в списке
      const { routeId, updates } = action.payload;
      if (!routeId || !updates) {
        return;
      }
      
      // Гарантируем, что state.routes всегда массив
      if (!Array.isArray(state.routes)) {
        state.routes = [];
        return;
      }
      
      const routeIndex = state.routes.findIndex(r => r && r.id === routeId);
      if (routeIndex !== -1) {
        // Обновляем маршрут, сохраняя существующие поля
        state.routes[routeIndex] = {
          ...state.routes[routeIndex],
          ...updates
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch routes
      .addCase(fetchRoutesInArea.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoutesInArea.fulfilled, (state, action) => {
        state.isLoading = false;
        // Извлекаем массив маршрутов из PaginationResponse
        const routes = action.payload?.data || action.payload;
        // Гарантируем, что всегда устанавливается массив
        state.routes = Array.isArray(routes) ? routes : [];
      })
      .addCase(fetchRoutesInArea.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch videos
      .addCase(fetchVideosInArea.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVideosInArea.fulfilled, (state, action) => {
        state.isLoading = false;
        // Извлекаем массив видео из PaginationResponse
        const videos = action.payload?.data || action.payload || [];
        state.videos = Array.isArray(videos) ? videos : [];
      })
      .addCase(fetchVideosInArea.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch all videos
      .addCase(fetchAllVideos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllVideos.fulfilled, (state, action) => {
        state.isLoading = false;
        // Извлекаем массив видео из PaginationResponse
        const videos = action.payload?.data || action.payload || [];
        const videosArray = Array.isArray(videos) ? videos : [];
        
        // Если offset = 0, заменяем все видео, иначе добавляем к существующим
        const offset = action.meta.arg?.offset || 0;
        if (offset === 0) {
          state.videos = videosArray;
        } else {
          // Добавляем новые видео, избегая дубликатов
          const existingIds = new Set(state.videos.map(v => v?.id).filter(Boolean));
          const newVideos = videosArray.filter(v => v?.id && !existingIds.has(v.id));
          state.videos = [...state.videos, ...newVideos];
        }
        
        // Обновляем информацию о пагинации из PaginationResponse
        if (action.payload?.has_more !== undefined) {
          state.videosPagination.hasMore = action.payload.has_more;
        } else {
          // Fallback: если has_more нет, определяем по количеству загруженных видео
          const limit = action.meta.arg?.limit || 20;
          state.videosPagination.hasMore = videosArray.length === limit;
        }
        
        if (action.payload?.next_offset !== undefined) {
          state.videosPagination.currentOffset = action.payload.next_offset;
        } else {
          state.videosPagination.currentOffset = offset + videosArray.length;
        }
        
        if (action.payload?.total !== undefined) {
          state.videosPagination.total = action.payload.total;
        }
        
        if (action.payload?.limit !== undefined) {
          state.videosPagination.limit = action.payload.limit;
        }
      })
      .addCase(fetchAllVideos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch user routes
      .addCase(fetchUserRoutes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserRoutes.fulfilled, (state, action) => {
        state.isLoading = false;
        // Проверяем, что payload является массивом
        const routes = Array.isArray(action.payload) ? action.payload : [];
        // Гарантируем, что state.routes всегда массив перед использованием
        if (!Array.isArray(state.routes)) {
          console.warn('fetchUserRoutes.fulfilled: state.routes is not an array, initializing as empty array', state.routes);
          state.routes = [];
        }
        // Объединяем маршруты пользователя с существующими, убирая дубликаты
        const existingIds = new Set(state.routes.map(r => r && r.id).filter(Boolean));
        const newRoutes = routes.filter(route => route && route.id && !existingIds.has(route.id));
        state.routes = [...state.routes, ...newRoutes];
      })
      .addCase(fetchUserRoutes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedMap,
  setCurrentView,
  setSelectedRoute,
  setSelectedVideo,
  clearSelection,
  clearError,
  addRoute,
  removeRoute,
  clearVideos,
  updateVideoInList,
  updateRouteInList,
} = mapSlice.actions;

export default mapSlice.reducer;